import { FC, PropsWithChildren, createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import * as SizeTypeAPI from 'services/size-types';
import { EquipmentType } from 'types/damage';
import { SizeTypeLookupOption, SizeTypeOption } from 'types/size-types';

/* [label, [code]] entry shape — drop-in for the old `Object.entries(<dict>SizeTypes)` consumers */
export type SizeTypeEntry = [string, string[]];

type SizeTypeContextType = {
  /* active options (incl. equipmentType), sorted by label — selection-time dropdown source */
  sizeTypeOptions: SizeTypeOption[];
  /* full rows incl. inactive — admin grid */
  sizeTypeList: SizeTypeLookupOption[];
  /* active atomic entries grouped by equipment type (combos are a booking-only overlay) */
  containerSizeTypeEntries: SizeTypeEntry[];
  chassisSizeTypeEntries: SizeTypeEntry[];
  gensetSizeTypeEntries: SizeTypeEntry[];
  /* single shared size-code -> equipment-type lookup (replaces the 3 duplicated impls); same answer for every role */
  getEquipmentType: (code?: string | null) => EquipmentType | undefined;
  /* the catalog's reefer flag — never the code's spelling. Unknown code: false */
  isReefer: (code?: string | null) => boolean;
  /* catalog class GENSET. Unknown code: false */
  isGenset: (code?: string | null) => boolean;
  saveSizeType: (option: Partial<SizeTypeLookupOption>) => Promise<void>;
  deleteSizeType: (id: number) => Promise<void>;
};

export const SizeTypeContext = createContext<SizeTypeContextType>({
  sizeTypeOptions: [],
  sizeTypeList: [],
  containerSizeTypeEntries: [],
  chassisSizeTypeEntries: [],
  gensetSizeTypeEntries: [],
  getEquipmentType: () => undefined,
  isReefer: () => false,
  isGenset: () => false,
  saveSizeType: async () => {},
  deleteSizeType: async () => {}
});

export const useSizeTypes = () => useContext(SizeTypeContext);

export const SizeTypeProvider: FC<PropsWithChildren> = ({ children }) => {
  /* every row incl. retired, for every role: lookups read this, dropdowns read its active subset */
  const [catalog, setCatalog] = useState<SizeTypeOption[]>([]);
  const [sizeTypeList, setSizeTypeList] = useState<SizeTypeLookupOption[]>([]);

  const refresh = useCallback(async () => {
    setCatalog(await SizeTypeAPI.getSizeTypeCatalog());
    /* admin-only endpoint, for the admin grid only; non-admins silently get [] (fetchWithRetry default) */
    setSizeTypeList(await SizeTypeAPI.getSizeTypeList());
  }, []);

  /* retired rows are for reading history, never for a new selection. Absent `active` = older backend = active */
  const sizeTypeOptions = useMemo(() => catalog.filter(o => o.active !== false), [catalog]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const entriesFor = useCallback(
    (equipmentType: EquipmentType): SizeTypeEntry[] =>
      sizeTypeOptions.filter(o => o.equipmentType === equipmentType).map(o => [o.label, [o.code]] as SizeTypeEntry),
    [sizeTypeOptions]
  );

  const containerSizeTypeEntries = useMemo(() => entriesFor(EquipmentType.CONTAINER), [entriesFor]);
  const chassisSizeTypeEntries = useMemo(() => entriesFor(EquipmentType.CHASSIS), [entriesFor]);
  const gensetSizeTypeEntries = useMemo(() => entriesFor(EquipmentType.GENSET), [entriesFor]);

  /*
   * Built from the full catalog, which every role can read, so a retired code on a historical record
   * resolves the same for a gate user as for an admin (F14). It used to prefer the admin-only list,
   * which is empty for gate roles.
   */
  const byCode = useMemo(() => {
    const map = new Map<string, SizeTypeOption>();
    catalog.forEach(o => o.code && map.set(o.code, o));
    return map;
  }, [catalog]);

  const getEquipmentType = useCallback(
    (code?: string | null): EquipmentType | undefined => (code ? byCode.get(code)?.equipmentType : undefined),
    [byCode]
  );

  const isReefer = useCallback((code?: string | null): boolean => !!code && !!byCode.get(code)?.reefer, [byCode]);

  const isGenset = useCallback(
    (code?: string | null): boolean => !!code && byCode.get(code)?.equipmentType === EquipmentType.GENSET,
    [byCode]
  );

  const saveSizeType = useCallback(
    async (option: Partial<SizeTypeLookupOption>) => {
      if (option.id) await SizeTypeAPI.updateSizeType(option as SizeTypeLookupOption);
      else await SizeTypeAPI.saveSizeType(option); /* throws DuplicateSizeTypeCodeError on 409 */
      await refresh();
    },
    [refresh]
  );

  const deleteSizeType = useCallback(
    async (id: number) => {
      await SizeTypeAPI.deleteSizeType(id);
      await refresh();
    },
    [refresh]
  );

  return (
    <SizeTypeContext.Provider
      value={{
        sizeTypeOptions,
        sizeTypeList,
        containerSizeTypeEntries,
        chassisSizeTypeEntries,
        gensetSizeTypeEntries,
        getEquipmentType,
        isReefer,
        isGenset,
        saveSizeType,
        deleteSizeType
      }}
    >
      {children}
    </SizeTypeContext.Provider>
  );
};
